// src/lib/referral/referral-terms.ts
//
// What a referral actually gives, stated once and used everywhere.
//
// The terms were written seven ways and no two agreed (audit 2026-09-11): the
// referral page headline said "Send a friend 1 month free", its detail said
// "your friend gets their 2nd month free when they sign up for a paid plan",
// the dashboard card promised free Pro "when your friend signs up and
// completes their first test", and applying a code said "Complete a test to
// unlock rewards for both of you". No test is involved.
//
// What the code does (src/lib/referral/referral-stripe.ts):
//   1. The friend subscribes to a paid plan and their first invoice is paid.
//   2. Just before their SECOND invoice, each side gets a Stripe balance credit
//      worth one month of Pro (PLANS.PRO.price), applied to their next bill.
//      On a monthly plan that makes the friend's second month free; on a
//      6-month or annual plan the credit lands at that renewal.
//   3. The referrer is credited only while on a paid Pro subscription with
//      Stripe billing — complimentary access does not receive the credit.

import { formatUsd, PLANS } from '@/lib/billing/plan-config'

/** The credit each side receives, in dollars — the same figure the Stripe handler issues. */
export const REFERRAL_CREDIT_USD = PLANS.PRO.price

const credit = formatUsd(REFERRAL_CREDIT_USD)

export const REFERRAL_TERMS = {
  /** Referrer-facing headline: the reward AND its condition, together. */
  headline: 'When a friend subscribes to Pro, you both get a month free.',

  /**
   * Friend-facing headline (invite banner, email). Names the credit rather than
   * "your second month": that is only true on a monthly plan, and checkout
   * defaults to annual, where the credit is applied at renewal.
   */
  friendHeadline: `Subscribe to Pro and get a month free — a ${credit} credit on your next bill.`,

  /** The full terms, for the referral page. */
  detail:
    `When your friend subscribes to Pro, you each get a ${credit} credit — one month of Pro. ` +
    `It's issued when their first paid period ends (after a month on a monthly plan, at renewal ` +
    `on 6-month and annual plans) and applied to your next bill. You receive yours while you ` +
    `have a paid Pro subscription; complimentary access doesn't qualify.`,

  /** Pre-filled share text. */
  shareText: (link: string) =>
    `I use UnlimitedTests for SAT prep. Sign up with my link — subscribe to Pro and you get a month free (a ${credit} credit): ${link}`,

  /** Shorter share text for SMS / WhatsApp. */
  shareTextShort: (link: string) =>
    `I use UnlimitedTests for SAT prep. Subscribe to Pro with my link and get a month free: ${link}`,

  /** Shown right after a referral code is applied. */
  applied: `Referral code applied. Subscribe to Pro and you'll get a ${credit} credit — a month free — when your first paid period ends.`,
} as const
